import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListVolunteersQueryKey,
  useListVolunteers,
  useCreateVolunteer,
  useUpdateVolunteer,
  type Volunteer
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { HeartHandshakeIcon, PlusIcon, ShieldCheckIcon, PencilIcon } from 'lucide-react';

function isExpired(dateStr: string): boolean {
  return new Date(dateStr) < new Date();
}

function expiryBadge(dateStr: string) {
  const expired = isExpired(dateStr);
  const daysLeft = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);

  if (expired) {
    return <Badge className="border-destructive/20 bg-destructive/10 text-destructive">Expired</Badge>;
  }

  if (daysLeft <= 30) {
    return <Badge className="border-warning/20 bg-warning/15 text-warning-foreground">Expires in {daysLeft}d</Badge>;
  }

  return <Badge className="border-success/20 bg-success/10 text-success">Valid</Badge>;
}

export function VolunteersPage() {
  const { data, isLoading } = useListVolunteers();
  const volunteers = data?.volunteers ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Volunteer Compliance"
        description="Working with Children Check (WWCC) verification and induction tracking"
        icon={HeartHandshakeIcon}
        actions={<CreateVolunteerDialog />}
      />
      <Card className="overflow-hidden border-border/80 bg-card py-0 shadow-sm shadow-primary/5">
        <Table className="[&_thead]:bg-muted/70 [&_thead_th]:h-12 [&_thead_th]:px-4 [&_thead_th]:text-xs [&_thead_th]:font-semibold [&_thead_th]:uppercase [&_thead_th]:tracking-wider [&_tbody_td]:px-4 [&_tbody_td]:py-4">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>WWCC Number</TableHead>
              <TableHead>WWCC Expiry</TableHead>
              <TableHead>Induction</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  Loading volunteers…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && volunteers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  No volunteers registered yet.
                </TableCell>
              </TableRow>
            )}
            {volunteers.map((v: Volunteer) => (
              <VolunteerRow key={v.id} volunteer={v} />
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function VolunteerRow({ volunteer }: { volunteer: Volunteer }) {
  const queryClient = useQueryClient();
  const patchMutation = useUpdateVolunteer();
  const [editOpen, setEditOpen] = React.useState(false);

  const toggleInduction = async () => {
    await patchMutation.mutateAsync({
      id: volunteer.id,
      data: { isInducted: !volunteer.isInducted }
    });
    await queryClient.invalidateQueries({ queryKey: getListVolunteersQueryKey() });
  };

  return (
    <>
      <TableRow className="group hover:bg-primary/5 focus-within:bg-primary/5">
        <TableCell className="font-semibold">{volunteer.name}</TableCell>
        <TableCell className="text-muted-foreground">{volunteer.phone}</TableCell>
        <TableCell className="font-mono text-sm">{volunteer.wwccNumber}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {new Date(volunteer.wwccExpiry).toLocaleDateString('en-AU')}
            </span>
            {expiryBadge(volunteer.wwccExpiry)}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Switch
              checked={volunteer.isInducted}
              onCheckedChange={toggleInduction}
              disabled={patchMutation.isPending}
              aria-label={`Mark ${volunteer.name} as ${volunteer.isInducted ? 'not inducted' : 'inducted'}`}
              className="data-[state=checked]:bg-success"
            />
            <Badge
              className={
                volunteer.isInducted
                  ? 'border-success/20 bg-success/10 text-success'
                  : 'border-border bg-muted text-muted-foreground'
              }
            >
              {volunteer.isInducted && <ShieldCheckIcon className="mr-1 size-3.5" />}
              {volunteer.isInducted ? 'Inducted' : 'Pending'}
            </Badge>
          </div>
        </TableCell>
        <TableCell className="text-right">
          <Button
            variant="outline"
            size="sm"
            className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
            onClick={() => setEditOpen(true)}
          >
            <PencilIcon className="size-3.5" />
            Edit
          </Button>
        </TableCell>
      </TableRow>

      <EditVolunteerDialog
        volunteer={volunteer}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
    </>
  );
}

function EditVolunteerDialog({
  volunteer,
  open,
  onOpenChange
}: {
  volunteer: Volunteer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const mutation = useUpdateVolunteer();

  const [form, setForm] = React.useState({
    name: volunteer.name,
    phone: volunteer.phone,
    wwccNumber: volunteer.wwccNumber,
    wwccExpiry: volunteer.wwccExpiry.split('T')[0],
    isInducted: volunteer.isInducted
  });

  React.useEffect(() => {
    if (open) {
      setForm({
        name: volunteer.name,
        phone: volunteer.phone,
        wwccNumber: volunteer.wwccNumber,
        wwccExpiry: volunteer.wwccExpiry.split('T')[0],
        isInducted: volunteer.isInducted
      });
    }
  }, [open, volunteer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mutation.mutateAsync({
      id: volunteer.id,
      data: {
        name: form.name,
        phone: form.phone,
        wwccNumber: form.wwccNumber,
        wwccExpiry: form.wwccExpiry,
        isInducted: form.isInducted
      }
    });
    await queryClient.invalidateQueries({ queryKey: getListVolunteersQueryKey() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Volunteer</DialogTitle>
            <DialogDescription>Update volunteer contact and WWCC details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="e-v-name">Full Name</Label>
              <Input id="e-v-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-v-phone">Phone (E.164)</Label>
              <Input id="e-v-phone" placeholder="+61412345678" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-v-wwcc">WWCC Number</Label>
              <Input id="e-v-wwcc" value={form.wwccNumber} onChange={(e) => setForm({ ...form, wwccNumber: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="e-v-expiry">WWCC Expiry Date</Label>
              <Input id="e-v-expiry" type="date" value={form.wwccExpiry} onChange={(e) => setForm({ ...form, wwccExpiry: e.target.value })} required />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="e-v-inducted" checked={form.isInducted} onCheckedChange={(c) => setForm({ ...form, isInducted: c })} />
              <Label htmlFor="e-v-inducted">Induction completed</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateVolunteerDialog() {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const mutation = useCreateVolunteer();

  const [form, setForm] = React.useState({
    name: '',
    phone: '',
    wwccNumber: '',
    wwccExpiry: '',
    isInducted: false
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mutation.mutateAsync({ data: form });
    await queryClient.invalidateQueries({ queryKey: getListVolunteersQueryKey() });
    setForm({ name: '', phone: '', wwccNumber: '', wwccExpiry: '', isInducted: false });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="mr-2 size-4" />
          Add Volunteer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>New Volunteer</DialogTitle>
            <DialogDescription>Register a volunteer with their WWCC details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="v-name">Full Name</Label>
              <Input id="v-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-phone">Phone (E.164)</Label>
              <Input id="v-phone" placeholder="+61412345678" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-wwcc">WWCC Number</Label>
              <Input id="v-wwcc" value={form.wwccNumber} onChange={(e) => setForm({ ...form, wwccNumber: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="v-expiry">WWCC Expiry Date</Label>
              <Input id="v-expiry" type="date" value={form.wwccExpiry} onChange={(e) => setForm({ ...form, wwccExpiry: e.target.value })} required />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="v-inducted" checked={form.isInducted} onCheckedChange={(c) => setForm({ ...form, isInducted: c })} />
              <Label htmlFor="v-inducted">Induction completed</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save Volunteer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
